import React from 'react';
import { Alert, Descriptions, Table, Tag } from 'antd';
import { useTranslate } from '@/translations';

const historicalIdentityRecipes = new Set([
  'outline-v1',
  'studio-grey-v1',
  'studio-grey-v2',
]);

export const identityObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

export const storedEvaluationChecks = (
  evaluation?: Record<string, unknown> | null,
) => {
  if (typeof evaluation?.checksJson !== 'string') return {};
  try {
    return identityObject(JSON.parse(evaluation.checksJson));
  } catch {
    return {};
  }
};

export const ResearchIdentityStatus = ({
  research,
  recipeVersion,
}: {
  research?: Record<string, unknown> | null;
  recipeVersion?: string | null;
}) => {
  const { t } = useTranslate();
  const snapshot = identityObject(research?.identity_snapshot);
  const decision = identityObject(snapshot.decision);
  const status = decision.status;
  const historical = historicalIdentityRecipes.has(recipeVersion ?? '');
  return (
    <div className="mb-3">
      <Tag
        color={
          status === 'Established'
            ? 'success'
            : status || historical
              ? 'warning'
              : undefined
        }
      >
        {t(
          status === 'Established'
            ? 'imagery.identityEstablished'
            : status === 'Unresolved'
              ? 'imagery.identityUnresolved'
              : status === 'Conflict'
                ? 'imagery.identityConflict'
                : historical
                  ? 'imagery.identityLegacy'
                  : 'imagery.identityNotAssessed',
        )}
      </Tag>
      {typeof decision.reason === 'string' ? <p>{decision.reason}</p> : null}
      {!status ? (
        <Alert
          type="info"
          message={t(
            historical
              ? 'imagery.identityLegacyDescription'
              : 'imagery.identityNotAssessedDescription',
          )}
        />
      ) : null}
    </div>
  );
};

export const CandidateIdentityAssessment = ({
  checks,
  recipeVersion,
}: {
  checks?: Record<string, unknown> | null;
  recipeVersion?: string | null;
}) => {
  const { t } = useTranslate();
  const identity = identityObject(checks?.identity_checks);
  const components = identityObject(checks?.component_scores);
  const text = (value: unknown) =>
    typeof value === 'string' ? value : t('imagery.notAvailable');
  return (
    <section className="mt-3" aria-label={t('imagery.imageIdentityCheck')}>
      <h4>{t('imagery.imageIdentityCheck')}</h4>
      {Object.keys(identity).length ? (
        <Descriptions
          size="small"
          column={1}
          items={[
            ...(['make', 'model', 'body'] as const).map((field) => ({
              key: field,
              label: t(`imagery.identityField.${field}`),
              children: (
                <Tag color={identity[field] === 'Match' ? 'success' : 'error'}>
                  {text(identity[field])}
                </Tag>
              ),
            })),
            {
              key: 'observed',
              label: t('imagery.observedBody'),
              children: text(identity.observed_body),
            },
            {
              key: 'reason',
              label: t('imagery.resultReason'),
              children: text(identity.reason),
            },
          ]}
        />
      ) : (
        <Tag>
          {t(
            historicalIdentityRecipes.has(recipeVersion ?? '')
              ? 'imagery.identityLegacy'
              : 'imagery.imageIdentityNotAssessed',
          )}
        </Tag>
      )}
      {Object.keys(components).length ? (
        <Table
          size="small"
          rowKey="field"
          pagination={false}
          dataSource={(
            [
              ['identity', 35],
              ['framing', 20],
              ['geometry', 20],
              ['style', 15],
              ['quality', 10],
            ] as const
          ).map(([field, weight]) => ({
            field,
            weight,
            score: components[field],
          }))}
          columns={[
            {
              title: t('imagery.qualityComponent'),
              dataIndex: 'field',
              render: (
                field:
                  | 'identity'
                  | 'framing'
                  | 'geometry'
                  | 'style'
                  | 'quality',
              ) => t(`imagery.quality.${field}`),
            },
            {
              title: t('imagery.weight'),
              dataIndex: 'weight',
              render: (weight: number) => `${weight}%`,
            },
            {
              title: t('imagery.score'),
              dataIndex: 'score',
              render: (score: unknown) =>
                typeof score === 'number' ? score : t('imagery.unknown'),
            },
          ]}
        />
      ) : null}
      <p className="imagery-caption">{t('imagery.qualityExplanation')}</p>
    </section>
  );
};
